<template>

  <div class="demo-img-uploader" :class="{
    'is-file-hovering': isFileHovering,
    'is-error': error
  }">
    <form
      v-if="!error"
      ref="form"
      v-on:drop.prevent="onDrop"
      v-on:dragenter.prevent="setDropActive"
      v-on:dragover.prevent="setDropActive"
      v-on:dragleave.prevent="setDropInactive"
    >
      <label for="img-uploader">
        <input
          id="img-uploader"
          ref="input"
          type="file"
          accept="image/png,image/jpg,.jpg,.png"
          v-on:change="onChange"
        >
      </label>
    </form>
    <div class="inner">
      <div class="demo-icon">
        <template v-if="error">
          <svgIconUploaderFailUnknown />
        </template>
        <template v-else>
          <svgIconUploader />
        </template>
      </div>
      <div class="demo-helper-text">
        {{helperText}}
      </div>
      <div class="demo-info-text">
        {{infoText}}
      </div>
      <div class="demo-btn-wrapper">
        <Button
          v-if="error"
          v-on:click="clearError"
          class="btn-reset"
        >
          <template v-slot:icon><svgRefresh/></template>
        </Button>
      </div>
    </div>
  </div>
  <div class="demo-img-buttons">
    <div class="grid">
      <Button
        color="primary"
        size="large"
        class="btn-sample"
        v-on:click="selectSample"
      >
        샘플 테스트
      </Button>
      <Button
        color="secondary"
        size="large"
        class="btn-upload"
        v-on:click="clickUploadImage"
      >
        이미지 업로드
        <template v-slot:icon><svgUpload /></template>
      </Button>
      <!-- <button class="btn-sample" v-on:click="selectSample">샘플 테스트</button>
      <button class="btn-upload" v-on:click="clickUploadImage">이미지 업로드 <svgUpload/></button> -->
    </div>
  </div>
</template>

<script>
import svgUpload from '@/svg/svgUpload'
import svgIconUploader from '@/svg/svgIconUploader'
import svgIconUploaderFailUnknown from '@/svg/svgIconUploaderFailUnknown'
import svgRefresh from '@/svg/svgRefresh'
import Button from '@/components/el/Button'

export default {
  props: {
    error: {
      type: [Boolean, Object, String],
      default: false,
    }
  },
  data() {
    return {
      currentImage: false,
      isImageLoading: false,
      isFileHovering: false,
      acceptedImages: [
        'image/jpeg', 'image/jpg', 'image/png'
      ]
    }
  },
  emits: [
    'selectSample',
    'clearError',
    'load',
    'unload',
    'imageLoaded',
    'error'
  ],
  methods: {
    clickUploadImage() {
      if (this.error) {
        this.clearError(); 
        this.$nextTick(()=> this.$refs.input.click())
      } else {
        this.$refs.input.click()
      }
    },
    selectSample() {
      this.$emit('selectSample')
    },
    clearError() {
      this.$emit('clearError')
      this.isImageLoading = false
    },
    setDropActive() {
      this.isFileHovering = true
    },
    setDropInactive() {
      this.isFileHovering = false
    },
    onDrop(e) {
      this.isFileHovering = false
      this.$emit('load')
      if (this.isImageLoading) return
      this.isImageLoading = true
      this.onFileSelect(e.dataTransfer.files[0])
    },
    onChange(e) {
      this.$emit('load')
      if (this.isImageLoading) return
      this.isImageLoading = true
      this.onFileSelect(e.target.files[0])
    },
    async onFileSelect(file) {
      try {
        let data = await this.readFile(file)
        this.$emit('imageLoaded', data)
      } catch (err) {
        if (err) {
          console.log('err', err)
          this.$emit('error', err)
          console.log('emit error')
        }
      } finally {
        this.isImageLoading = false
      }
    },
    async readFile(file) {
      return new Promise((resolve, reject)=> {
        const fileReader = new FileReader()
        fileReader.onload = async ()=> {
          if (!['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
            return reject('FILE_TYPE_UNACCEPTED')
          }
          const sizeMB = file.size / (1024 ** 2)
          console.log(sizeMB, file)
          if (sizeMB > 40) {
            return reject('FILE_SIZE_EXCEEDED')
          }
          return resolve({
            file: file,
            fileData: {
              name: file.name,
              size: file.size,
              type: file.type,
            },
            dataUrl: fileReader.result,
            dimensions: await this.getImageDimensions(fileReader.result)
          })
        }
        fileReader.readAsDataURL(file)
      })
    },
    getImageDimensions(dataUrl) {
      return new Promise((resolve)=> {
        const img = new Image()
        img.onload = ()=> {
          return resolve({
            height: img.height,
            width: img.width
          })
        }
        img.src = dataUrl
      })
    },
  },
  computed: {
    helperText() {
      if (this.error) {
        if (['FILE_SIZE_EXCEEDED', 'FILE_TYPE_UNACCEPTED'].includes(this.error)) {
          return "이미지 형식에 맞게 넣어주세요."
        } 
        return "죄송합니다. 알 수 없는 문제가 발생했습니다."
      }
      return  "내 PC에서 이미지를 올리거나\n이곳에 마우스로 끌어서 넣어주세요."
    },
    infoText() {
      if (this.error) {
        if (this.error === 'FILE_SIZE_EXCEEDED') {
          return "이미지는 최대 40MB까지 허용됩니다."
        } else if (this.error === 'FILE_TYPE_UNACCEPTED') { 
          return "이미지는 JPG 또는 PNG 포맷만 허용됩니다."
        }
        return "일시적인 현상이거나 인터넷 문제일 수 있으니,\n새로고침 후 다시 시도해 주세요."
      }
      return "사이즈 255x255px 이상 / 이미지 용량 40MB 이하 / JPG, PNG 파일을 지원합니다."
    }
  },
  components: {
    svgIconUploader,
    svgIconUploaderFailUnknown,
    svgRefresh,
    svgUpload,
    Button
  },
}

</script>

<style lang="scss" scoped>

.demo-img-uploader {
  position: relative;
  width: 100%;
  border: 1px dashed var(--gray-400);
  background: var(--gray-200);
  border-radius: 8px;
  height: 315px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  &.is-file-hovering {
    background: var(--gray-300);
  }

  &.is-error {
    background: #FEECD4;
    border: 1px solid #FD995E;
  }
  
  &:hover {
    border-color: var(--gray-500);
  }

  @media screen and (max-width: 940px) {
    height: 260px;
  }


}
.demo-img-uploader .inner {
  position: relative;
  z-index: 2;
  pointer-events: none;
  width: 100%;
   @media screen and (max-width: 640px) {
    padding: 24px 0;
  }
}

.demo-img-uploader form {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
.demo-img-uploader input {
  position: absolute;
  top: -9999px;
  left: -9999px;
  visibility: hidden;
}
.demo-img-uploader label {
  // position: absolute;
  // top: 0;
  // left: 0;
  cursor: pointer;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.demo-icon {
  width: 90px;
  height: 90px;
  margin: 0 auto;
  svg {
    display: block;
    width: auto;
    height: 100%;
    margin: auto;
  }
  @media screen and (max-width: 940px) {
    width: 80px;
    height: 80px;
  }
}

.demo-helper-text {
  font-weight: 700;
  font-size: var(--typ-body3);
  line-height: 1.445;
  text-align: center;
  color: var(--gray-900);
  margin: 16px 0;
  white-space: pre;
  @media screen and (max-width: 940px) {
    font-size: var(--typ-body4);
    margin: 6px 0;
    .is-error & {
      margin: 4px 0;
    }
  }
 
}

.demo-info-text {
  font-size: var(--typ-body5);
  color: var(--info-500);
  line-height: 1.5;
  text-align: center;
  white-space: pre-line;
  @media screen and (max-width: 640px) {
    font-size: 12px;
    padding: 0 40px;
  }
  .is-error & {
    color: #D84A1E;
  }
}


.demo-btn-wrapper {
  margin: 8px auto 0;
  display: flex;
  justify-content: center;
}



button.btn-reset {
  background-color: var(--primary-500);
  border: 0;
  border-radius: 8px;
  width: 44px;
  height: 44px;
  pointer-events: auto;
  &:hover {
    background-color: var(--primary-600);
  }
  &:active {
    background-color: var(--primary-700);
  }
}

.demo-img-buttons {
  padding-top: 24px;
  @media screen and (max-width: 940px) {
    // padding: 0;
    
  }
}

.demo-img-buttons .grid {
  // @media screen and (max-width: 940px) {
  //   display: flex;
  //   align-items: center;
  //   flex-direction: column-reverse;
  //   grid-gap: 18px;
  //   padding-top: 18px;
  // }
  @media screen and (max-width: 640px) {
    display: flex;
    flex-direction: row;
    grid-gap: 8px;
  }
}

.demo-img-buttons button {
  border: 0;
  height: 56px;
  border-radius: 8px;
  @media screen and (max-width: 940px) {
    width: 210px;
    // grid-column: none;
  }
  @media screen and (max-width: 768px) {
    font-size: 18px;
  }
  @media screen and (max-width: 640px) {
    flex: 1;
    padding: 0 8px;
    &:deep(.icon) {
      margin-left: 10px;
    }
  }
  @media screen and (max-width: 420px) {
    font-size: 16px
  }
  @media screen and (max-width: 320px) {
    font-size: 14px;
  }
}

button.btn-sample {
  grid-column-start: 4;
  grid-column-end: 7;
}
button.btn-upload {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  grid-column-start: 7;
  grid-column-end: 10;
  padding: 0;
}

</style>